/**
 * Run trigger / list / detail shapes.
 *
 * Endpoints (per IMPLEMENTATION.md §9.1):
 *   POST  /api/v1/agents/:id/runs   → TriggerRunRequest  → TriggerRunResponse (202)
 *   GET   /api/v1/agents/:id/runs   → ListRunsQuery     → ListRunsResponse
 *   GET   /api/v1/runs              → ListRunsQuery     → ListRunsResponse
 *   GET   /api/v1/runs/:id          → RunResponse
 *   POST  /api/v1/runs/:id/cancel   → 204
 *
 * Run wire shape mirrors the public-API columns on `runs` (ADR-004 calls these
 * out as public API). Source of truth for the DB shape lives in
 * `@simpliwork/db`; we redeclare here as Zod so this package has no DB
 * dependency.
 */
import { z } from "zod";
import { BackendIdSchema } from "./agents.js";
import { IdempotencyKeySchema, IsoTimestampSchema, paginatedResponse, UuidSchema, } from "./common.js";
import { ErrorPayloadSchema } from "./errors.js";
/** Lifecycle states for a run. Mirrors `RunStatus['state']` in executor-core. */
export const RunStatusEnumSchema = z.enum([
    "pending",
    "running",
    "completed",
    "failed",
    "cancelled",
    "timeout",
]);
/**
 * Canonical LLM-conversation role values (RLY-239 / standing order [33]).
 *
 * The Anthropic Messages API (and similarly-shaped LLM APIs) tag each
 * conversational turn with a `role` literal — `"user" | "assistant" |
 * "system"`. Per [33], production code that emits one of these literals
 * MUST consume `MessageRole.<X>` from this canonical module rather than
 * inlining the string. Tests that mock the API shape get allow-listed.
 *
 * Why this lives in `api-schema/runs.ts`:
 *   - `runs.ts` is the natural home for run/message wire shapes.
 *   - `@simpliwork/api-schema` is already a workspace dep of every
 *     production consumer (portal-frontend, portal-api, executor-core
 *     transitively). No new dep edges are introduced by adopting it.
 *   - The constant is type-level + value-level in a single declaration,
 *     so consumers can either narrow against `MessageRole` (the type)
 *     or import `MessageRole.User` (the value).
 *
 * NOT a tenant role (per ADR-015). Tenant roles are `owner|admin|operator|
 * viewer` (DB-side) / `owner|admin|member` (API-side). LLM message roles
 * are an entirely separate vocabulary — the role-vocab-guard intentionally
 * routes around this constant via the existing `packages/executor-anthropic/`
 * + (post-RLY-239) constant-consumer allow-list semantics.
 *
 * Phase-1 ships `user | assistant | system`. RLY-211's potential 4th value
 * (`tool`) is reserved for Phase-2; landing it requires updating this
 * constant + every consumer in lock-step (per [21]+[25]'s structural
 * consistency gate).
 */
export const MessageRole = {
    User: "user",
    Assistant: "assistant",
    System: "system",
};
/** Zod schema mirroring the `MessageRole` value union for runtime validation. */
export const MessageRoleSchema = z.enum([
    MessageRole.User,
    MessageRole.Assistant,
    MessageRole.System,
]);
export const RunUsageSchema = z.object({
    input_tokens: z.number().int().min(0),
    output_tokens: z.number().int().min(0),
    cost_micros: z.number().int().min(0),
});
/**
 * RLY-291 (Sprint-12 hotfix #10) — narrow `input` from `z.unknown()` to a
 * union mirroring the AnthropicMessagesExecutor's runtime contract at
 * `packages/executor-anthropic/src/executor.ts:225` (the `extractPrompt`
 * helper). The executor accepts EITHER a bare prompt string OR an object
 * `{ prompt: string }`; any other shape throws `invalid_input` at dispatch.
 *
 * Pre-narrow: a wrong-shape body (e.g. `input: {}`, `input: { message: "..." }`)
 * persisted a `runs` row that then failed at dispatch with the executor's
 * post-create error — bad UX (creation succeeded; run shows up in the list
 * with a deferred failure).
 *
 * Post-narrow: POST `/api/v1/runs` returns 400 immediately with a Zod
 * validation error citing the expected shape. The `.strict()` modifier on
 * the object variant rejects extra keys (e.g. `{ prompt: "x", extra: "y" }`)
 * — closing the third creation-time-validation gap surfaced in the
 * RLY-289 → RLY-290 → RLY-291 chain.
 *
 * The DB-side `runs.input` column remains `jsonb` and the orchestrator's
 * `data.input` payload type stays `unknown` (see
 * `apps/orchestrator/src/inngest-client.ts:46` + `run-agent.ts:215`+`:273`)
 * — this narrow only tightens the API boundary, not internal storage shape.
 */
export const TriggerRunRequestSchema = z.object({
    input: z.union([z.string(), z.object({ prompt: z.string() }).strict()]),
    idempotency_key: IdempotencyKeySchema.optional(),
    metadata: z.record(z.string()).optional(),
});
/** 202 Accepted body when a run is queued. */
export const TriggerRunResponseSchema = z.object({
    run_id: UuidSchema,
    status: z.literal("pending"),
});
/** Full run record. Mirrors public-API columns on the `runs` table (ADR-004). */
export const RunResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    agent_id: UuidSchema,
    agent_version: z.number().int().min(1),
    backend: BackendIdSchema,
    status: RunStatusEnumSchema,
    input: z.unknown(),
    output: z.unknown().nullable(),
    error: ErrorPayloadSchema.nullable(),
    usage: RunUsageSchema,
    cost_finalized: z.boolean(),
    triggered_by: z.string(),
    trace_id: z.string(),
    created_at: IsoTimestampSchema,
    started_at: IsoTimestampSchema.nullable(),
    completed_at: IsoTimestampSchema.nullable(),
});
/** Query params for list endpoints. Filters narrow within the active tenant. */
export const ListRunsQuerySchema = z.object({
    agent_id: UuidSchema.optional(),
    status: RunStatusEnumSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const ListRunsResponseSchema = paginatedResponse(RunResponseSchema);
// ── Cost events (M3.5 — run detail polish) ───────────────────────────────────
export const CostEventSchema = z.object({
    id: z.string().uuid(),
    source: z.string(), // "anthropic" | "fly" | "mcp" | "inngest" | "storage"
    detail: z.string().nullable(), // e.g. "claude-sonnet-4-input"
    units: z.string(), // numeric stringified — preserves precision
    unit_cost_micros: z.number().int().min(0),
    cost_micros: z.number().int().min(0),
    at: IsoTimestampSchema,
    metadata: z.record(z.string(), z.unknown()).default({}),
});
export const RunCostEventsResponseSchema = z.object({
    items: z.array(CostEventSchema),
    total_cost_micros: z.number().int().min(0),
});
//# sourceMappingURL=runs.js.map